/**
 * Accessibility Testing Utilities for Shopify Theme
 * 
 * Usage in Console:
 *   window.A11yTest.headingHierarchy()
 *   window.A11yTest.landmarkRoles()
 *   window.A11yTest.ariaAttributes()
 *   window.A11yTest.liveRegions()
 *   window.A11yTest.runAll()
 */

(function() {
  'use strict';

  var A11yTest = {
    
    /**
     * Test 1: Heading Hierarchy
     */
    headingHierarchy: function() {
      console.clear();
      console.log('%c=== HEADING HIERARCHY TEST ===', 'color: #2196F3; font-size: 16px; font-weight: bold;');
      
      var headings = document.querySelectorAll('h1,h2,h3,h4,h5,h6');
      var headingsArray = Array.prototype.slice.call(headings);
      var issues = [];
      
      console.log('\n📊 Found ' + headings.length + ' headings:\n');
      
      var lastLevel = 0;
      headingsArray.forEach(function(h, i) {
        var level = parseInt(h.tagName.charAt(1));
        var text = h.innerText.trim().substring(0, 50);
        var indent = '';
        
        for (var j = 0; j < level - 1; j++) {
          indent += '  ';
        }
        
        // Check for skipped levels
        if (lastLevel > 0 && level > lastLevel + 1) {
          console.log('%c⚠️ ' + h.tagName + ': "' + text + '"  [Skipped from H' + lastLevel + ']', 
            'color: #FF9800; font-weight: bold');
          issues.push('Line ' + (i + 1) + ': Skipped from H' + lastLevel + ' to H' + level);
        } else {
          console.log(indent + h.tagName + ': "' + text + '"');
        }
        
        lastLevel = level;
      });
      
      // Check H1 count
      var h1Count = document.querySelectorAll('h1').length;
      console.log('\n📌 H1 Tags: ' + h1Count);
      
      if (h1Count === 1) {
        console.log('%c✅ Perfect: Exactly 1 H1 tag', 'color: #4CAF50; font-weight: bold');
      } else if (h1Count > 1) {
        console.log('%c⚠️ Warning: ' + h1Count + ' H1 tags found (should have only 1)', 
          'color: #FF9800; font-weight: bold');
        issues.push('Multiple H1 tags (' + h1Count + ')');
      } else {
        console.log('%c❌ Error: No H1 tag found', 'color: #F44336; font-weight: bold');
        issues.push('No H1 tag');
      }
      
      // Summary
      console.log('\n=== SUMMARY ===');
      if (issues.length === 0) {
        console.log('%c✅ Perfect heading hierarchy!', 'color: #4CAF50; font-size: 14px; font-weight: bold');
      } else {
        console.log('%c⚠️ ' + issues.length + ' issue(s) found:', 'color: #FF9800; font-weight: bold');
        issues.forEach(function(issue) {
          console.log('  • ' + issue);
        });
      }
      
      return { passed: issues.length === 0, issues: issues };
    },

    /**
     * Test 2: Landmark Roles
     */
    landmarkRoles: function() {
      console.clear();
      console.log('%c=== LANDMARK ROLES TEST ===', 'color: #9C27B0; font-size: 16px; font-weight: bold;');
      
      var landmarks = [
        { selector: 'header[role="banner"]', name: '🏠 Banner (Header)', required: true },
        { selector: 'main[role="main"]', name: '📄 Main Content', required: true },
        { selector: 'footer[role="contentinfo"]', name: '📮 Footer', required: true },
        { selector: 'nav', name: '🧭 Navigation', required: false }
      ];
      
      console.log('\n📍 Checking landmark regions:\n');
      
      var allGood = true;
      var results = [];
      
      landmarks.forEach(function(landmark) {
        var elements = document.querySelectorAll(landmark.selector);
        
        if (elements.length > 0) {
          console.log('%c✅ ' + landmark.name + ': ' + elements.length + ' found', 'color: #4CAF50');
          results.push({ name: landmark.name, count: elements.length, found: true });
          
          // Show labels for nav elements
          if (landmark.selector === 'nav') {
            Array.prototype.forEach.call(elements, function(nav, i) {
              var label = nav.getAttribute('aria-label') || nav.getAttribute('aria-labelledby') || '⚠️ No label';
              var hasLabel = nav.getAttribute('aria-label') || nav.getAttribute('aria-labelledby');
              var color = hasLabel ? 'color: #666' : 'color: #FF9800';
              console.log('%c   ' + (i + 1) + '. ' + label, color);
            });
          }
        } else {
          if (landmark.required) {
            console.log('%c❌ ' + landmark.name + ': MISSING (Required!)', 'color: #F44336; font-weight: bold');
            allGood = false;
            results.push({ name: landmark.name, found: false, required: true });
          } else {
            console.log('⚪ ' + landmark.name + ': Not found (Optional)');
          }
        }
      });
      
      // Summary
      console.log('\n=== SUMMARY ===');
      if (allGood) {
        console.log('%c✅ All required landmarks present!', 'color: #4CAF50; font-size: 14px; font-weight: bold');
        console.log('\n📚 WCAG 2.1 Compliance: PASS');
      } else {
        console.log('%c❌ Missing required landmarks', 'color: #F44336; font-weight: bold');
      }
      
      // Show structure
      console.log('\n📐 Page Structure:');
      console.log('├─ <header role="banner">');
      console.log('│  └─ <nav> Navigation');
      console.log('├─ <main role="main">');
      console.log('│  └─ Page content');
      console.log('└─ <footer role="contentinfo">');
      
      return { passed: allGood, results: results };
    },

    /**
     * Test 3: ARIA Attributes
     */
    ariaAttributes: function() {
      console.clear();
      console.log('%c=== ARIA ATTRIBUTES TEST ===', 'color: #FF6B6B; font-size: 16px; font-weight: bold;');
      
      var issues = [];
      
      console.log('\n🔍 Checking ARIA attributes:\n');
      
      // Check aria-expanded without aria-controls
      var expandedElements = document.querySelectorAll('[aria-expanded]');
      console.log('📌 Elements with aria-expanded: ' + expandedElements.length);
      
      var missingControls = 0;
      Array.prototype.forEach.call(expandedElements, function(el) {
        if (!el.getAttribute('aria-controls')) {
          missingControls++;
          console.log('%c⚠️ Missing aria-controls:', 'color: #FF9800', el);
        }
      });
      
      if (missingControls === 0) {
        console.log('%c✅ All aria-expanded elements have aria-controls', 'color: #4CAF50');
      } else {
        console.log('%c⚠️ ' + missingControls + ' elements missing aria-controls', 'color: #FF9800');
        issues.push(missingControls + ' elements with aria-expanded missing aria-controls');
      }
      
      // Check buttons without labels
      console.log('\n📌 Checking buttons for labels:');
      var buttons = document.querySelectorAll('button');
      var unlabeledButtons = 0;
      
      Array.prototype.forEach.call(buttons, function(btn) {
        var hasText = btn.innerText.trim().length > 0;
        var hasAriaLabel = btn.getAttribute('aria-label');
        var hasAriaLabelledby = btn.getAttribute('aria-labelledby');
        var hasTitle = btn.getAttribute('title');
        
        if (!hasText && !hasAriaLabel && !hasAriaLabelledby && !hasTitle) {
          unlabeledButtons++;
          console.log('%c⚠️ Button without label:', 'color: #FF9800', btn);
        }
      });
      
      if (unlabeledButtons === 0) {
        console.log('%c✅ All buttons have labels (' + buttons.length + ' checked)', 'color: #4CAF50');
      } else {
        console.log('%c⚠️ ' + unlabeledButtons + ' buttons without labels', 'color: #FF9800');
        issues.push(unlabeledButtons + ' buttons without accessible labels');
      }
      
      // Check links without labels
      console.log('\n📌 Checking links for labels:');
      var links = document.querySelectorAll('a');
      var unlabeledLinks = 0;
      
      Array.prototype.forEach.call(links, function(link) {
        var hasText = link.innerText.trim().length > 0;
        var hasAriaLabel = link.getAttribute('aria-label');
        var hasAriaLabelledby = link.getAttribute('aria-labelledby');
        var hasTitle = link.getAttribute('title');
        var hasImgAlt = link.querySelector('img[alt]');
        
        if (!hasText && !hasAriaLabel && !hasAriaLabelledby && !hasTitle && !hasImgAlt) {
          unlabeledLinks++;
          if (unlabeledLinks <= 5) {
            console.log('%c⚠️ Link without label:', 'color: #FF9800', link);
          }
        }
      });
      
      if (unlabeledLinks === 0) {
        console.log('%c✅ All links have labels (' + links.length + ' checked)', 'color: #4CAF50');
      } else {
        console.log('%c⚠️ ' + unlabeledLinks + ' links without labels (showing first 5)', 'color: #FF9800');
        issues.push(unlabeledLinks + ' links without accessible labels');
      }
      
      // Summary
      console.log('\n=== SUMMARY ===');
      if (issues.length === 0) {
        console.log('%c✅ All ARIA attributes properly implemented!', 'color: #4CAF50; font-size: 14px; font-weight: bold');
      } else {
        console.log('%c⚠️ ' + issues.length + ' issue(s) found:', 'color: #FF9800; font-weight: bold');
        issues.forEach(function(issue) {
          console.log('  • ' + issue);
        });
      }
      
      return { passed: issues.length === 0, issues: issues };
    },

    /**
     * Test 4: ARIA Live Regions
     */
    liveRegions: function() {
      console.clear();
      console.log('%c=== ARIA LIVE REGIONS TEST ===', 'color: #4ECDC4; font-size: 16px; font-weight: bold;');
      
      console.log('\n🔴 Checking live regions:\n');
      
      // Check for global live regions
      var statusRegion = document.getElementById('a11y-status-message');
      var alertRegion = document.getElementById('a11y-alert-message');
      var productFormStatus = document.getElementById('product-form-status');
      var productFormError = document.getElementById('product-form-error');
      
      console.log('📌 Global Live Regions:');
      console.log(statusRegion ? '%c✅ #a11y-status-message exists' : '%c❌ #a11y-status-message missing', 
        statusRegion ? 'color: #4CAF50' : 'color: #F44336');
      console.log(alertRegion ? '%c✅ #a11y-alert-message exists' : '%c❌ #a11y-alert-message missing',
        alertRegion ? 'color: #4CAF50' : 'color: #F44336');
      
      if (productFormStatus || productFormError) {
        console.log('\n📌 Product Form Live Regions:');
        console.log(productFormStatus ? '%c✅ #product-form-status exists' : '%c⚪ Not on product page',
          productFormStatus ? 'color: #4CAF50' : 'color: #999');
        console.log(productFormError ? '%c✅ #product-form-error exists' : '%c⚪ Not on product page',
          productFormError ? 'color: #4CAF50' : 'color: #999');
      }
      
      // Check all live regions
      var allLiveRegions = document.querySelectorAll('[aria-live], [role="status"], [role="alert"]');
      console.log('\n📌 All Live Regions Found: ' + allLiveRegions.length);
      
      Array.prototype.forEach.call(allLiveRegions, function(region, i) {
        var role = region.getAttribute('role');
        var ariaLive = region.getAttribute('aria-live');
        var id = region.id || '(no id)';
        var content = region.textContent.trim().substring(0, 50) || '(empty)';
        
        console.log((i + 1) + '. ' + (role || 'div') + ' [' + id + ']');
        console.log('   aria-live: ' + ariaLive + ' | Content: "' + content + '"');
      });
      
      // Check if announcement function exists
      console.log('\n📌 Announcement Function:');
      var hasFunction = typeof window.announceToScreenReader === 'function';
      console.log(hasFunction ? '%c✅ window.announceToScreenReader exists' : '%c❌ window.announceToScreenReader missing',
        hasFunction ? 'color: #4CAF50' : 'color: #F44336');
      
      if (hasFunction) {
        console.log('\n💡 Test it with:');
        console.log('   window.announceToScreenReader("Test message", "polite")');
        console.log('   window.announceToScreenReader("Alert message", "assertive")');
      }
      
      // Summary
      console.log('\n=== SUMMARY ===');
      var passed = statusRegion && alertRegion && hasFunction;
      if (passed) {
        console.log('%c✅ Live regions properly configured!', 'color: #4CAF50; font-size: 14px; font-weight: bold');
      } else {
        console.log('%c⚠️ Some live regions missing', 'color: #FF9800; font-weight: bold');
      }
      
      return { 
        passed: passed,
        regions: {
          global: { status: !!statusRegion, alert: !!alertRegion },
          function: hasFunction,
          total: allLiveRegions.length
        }
      };
    },

    /**
     * Test 5: Interactive Elements State
     */
    interactiveState: function() {
      console.clear();
      console.log('%c=== INTERACTIVE ELEMENTS TEST ===', 'color: #FFB347; font-size: 16px; font-weight: bold;');
      
      console.log('\n🎮 Checking interactive element states:\n');
      
      var issues = [];
      
      // Check collapsible elements
      var collapsibleButtons = document.querySelectorAll('button[aria-expanded], summary[aria-expanded]');
      console.log('📌 Collapsible Elements: ' + collapsibleButtons.length);
      
      if (collapsibleButtons.length > 0) {
        var expandedCount = 0;
        var collapsedCount = 0;
        
        Array.prototype.forEach.call(collapsibleButtons, function(btn) {
          var isExpanded = btn.getAttribute('aria-expanded') === 'true';
          if (isExpanded) expandedCount++;
          else collapsedCount++;
        });
        
        console.log('   Expanded: ' + expandedCount);
        console.log('   Collapsed: ' + collapsedCount);
        console.log('%c✅ All have aria-expanded attribute', 'color: #4CAF50');
      }
      
      // Check dialogs/modals
      var dialogs = document.querySelectorAll('[role="dialog"]');
      console.log('\n📌 Dialogs/Modals: ' + dialogs.length);
      
      Array.prototype.forEach.call(dialogs, function(dialog, i) {
        var labelledby = dialog.getAttribute('aria-labelledby');
        var label = dialog.getAttribute('aria-label');
        
        if (labelledby || label) {
          console.log('%c✅ Dialog ' + (i + 1) + ' has label', 'color: #4CAF50');
        } else {
          console.log('%c⚠️ Dialog ' + (i + 1) + ' missing label', 'color: #FF9800');
          issues.push('Dialog without aria-label or aria-labelledby');
        }
      });
      
      // Check form controls
      var formInputs = document.querySelectorAll('input, select, textarea');
      console.log('\n📌 Form Controls: ' + formInputs.length);
      
      var unlabeledInputs = 0;
      Array.prototype.forEach.call(formInputs, function(input) {
        var hasLabel = document.querySelector('label[for="' + input.id + '"]');
        var hasAriaLabel = input.getAttribute('aria-label');
        var hasAriaLabelledby = input.getAttribute('aria-labelledby');
        var isHidden = input.type === 'hidden';
        
        if (!isHidden && !hasLabel && !hasAriaLabel && !hasAriaLabelledby) {
          unlabeledInputs++;
        }
      });
      
      if (unlabeledInputs === 0) {
        console.log('%c✅ All form controls have labels', 'color: #4CAF50');
      } else {
        console.log('%c⚠️ ' + unlabeledInputs + ' form controls without labels', 'color: #FF9800');
        issues.push(unlabeledInputs + ' form controls without labels');
      }
      
      // Summary
      console.log('\n=== SUMMARY ===');
      if (issues.length === 0) {
        console.log('%c✅ All interactive elements properly configured!', 'color: #4CAF50; font-size: 14px; font-weight: bold');
      } else {
        console.log('%c⚠️ ' + issues.length + ' issue(s) found:', 'color: #FF9800; font-weight: bold');
        issues.forEach(function(issue) {
          console.log('  • ' + issue);
        });
      }
      
      return { passed: issues.length === 0, issues: issues };
    },

    /**
     * Run All Tests
     */
    runAll: function() {
      console.clear();
      console.log('%c════════════════════════════════════════════════', 'color: #333; font-weight: bold');
      console.log('%c    ACCESSIBILITY AUDIT - COMPLETE SUITE', 'color: #2196F3; font-size: 18px; font-weight: bold');
      console.log('%c════════════════════════════════════════════════', 'color: #333; font-weight: bold');
      console.log('\n');
      
      var results = {};
      
      // Test 1
      console.log('Running Test 1/5: Heading Hierarchy...\n');
      results.headingHierarchy = this.headingHierarchy();
      
      // Wait a bit between tests
      setTimeout(function() {
        console.log('\n\n');
        console.log('Running Test 2/5: Landmark Roles...\n');
        results.landmarkRoles = A11yTest.landmarkRoles();
        
        setTimeout(function() {
          console.log('\n\n');
          console.log('Running Test 3/5: ARIA Attributes...\n');
          results.ariaAttributes = A11yTest.ariaAttributes();
          
          setTimeout(function() {
            console.log('\n\n');
            console.log('Running Test 4/5: Live Regions...\n');
            results.liveRegions = A11yTest.liveRegions();
            
            setTimeout(function() {
              console.log('\n\n');
              console.log('Running Test 5/5: Interactive Elements...\n');
              results.interactiveState = A11yTest.interactiveState();
              
              // Final summary
              setTimeout(function() {
                console.log('\n\n');
                console.log('%c════════════════════════════════════════════════', 'color: #333; font-weight: bold');
                console.log('%c    FINAL RESULTS', 'color: #9C27B0; font-size: 18px; font-weight: bold');
                console.log('%c════════════════════════════════════════════════', 'color: #333; font-weight: bold');
                
                var passedTests = 0;
                var totalTests = 5;
                
                console.log('\n');
                console.log((results.headingHierarchy.passed ? '✅' : '❌') + ' Heading Hierarchy');
                console.log((results.landmarkRoles.passed ? '✅' : '❌') + ' Landmark Roles');
                console.log((results.ariaAttributes.passed ? '✅' : '❌') + ' ARIA Attributes');
                console.log((results.liveRegions.passed ? '✅' : '❌') + ' Live Regions');
                console.log((results.interactiveState.passed ? '✅' : '❌') + ' Interactive Elements');
                
                if (results.headingHierarchy.passed) passedTests++;
                if (results.landmarkRoles.passed) passedTests++;
                if (results.ariaAttributes.passed) passedTests++;
                if (results.liveRegions.passed) passedTests++;
                if (results.interactiveState.passed) passedTests++;
                
                console.log('\n');
                console.log('%c Score: ' + passedTests + '/' + totalTests + ' tests passed', 
                  'font-size: 16px; font-weight: bold; color: ' + (passedTests === totalTests ? '#4CAF50' : '#FF9800'));
                
                if (passedTests === totalTests) {
                  console.log('%c\n🎉 PERFECT! All accessibility tests passed!', 
                    'color: #4CAF50; font-size: 16px; font-weight: bold');
                } else {
                  console.log('%c\n⚠️ Some issues found. Review the details above.', 
                    'color: #FF9800; font-size: 14px; font-weight: bold');
                }
                
                console.log('\n');
              }, 100);
            }, 100);
          }, 100);
        }, 100);
      }, 100);
      
      return results;
    }
  };

  // Expose globally
  window.A11yTest = A11yTest;

  // Log initialization
  console.log('%c♿ Accessibility Testing Suite Loaded', 'color: #2196F3; font-weight: bold');
  console.log('%cUsage:', 'font-weight: bold');
  console.log('  window.A11yTest.headingHierarchy()   - Test heading structure');
  console.log('  window.A11yTest.landmarkRoles()      - Test landmark roles');
  console.log('  window.A11yTest.ariaAttributes()     - Test ARIA attributes');
  console.log('  window.A11yTest.liveRegions()        - Test live regions');
  console.log('  window.A11yTest.interactiveState()   - Test interactive elements');
  console.log('  window.A11yTest.runAll()             - Run all tests');
  console.log('\n');

})();

